"use client";

import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";
import { faqs } from "@/config/landing";
import { siteConfig } from "@/config/site";
import { cn } from "@/lib/utils";

export default function FAQSection() {
  return (
    <section className="py-12 sm:py-16 lg:py-24 bg-background">
      <MaxWidthWrapper>
        <div className="grid grid-cols-1 gap-10 md:gap-14 lg:grid-cols-2 lg:gap-20 xl:gap-24">
          {/* Left Column: Heading & Support Card */}
          <div className="flex flex-col justify-between space-y-8 lg:space-y-10">
            {/* Top Heading Group */}
            <div className="space-y-4">
              {/* Badge */}

              {/* Title */}
              <h2 className="text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl text-foreground font-urban">
                Have more questions?
              </h2>

              {/* Subtitle */}
              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed max-w-lg">
                Our software is engineered for stealth, rapid fulfillment, and seamless operation. Explore our most common inquiries or get in touch directly.
              </p>
            </div>

            {/* Bottom Support Card */}
            <div className="rounded-2xl border border-border/80 bg-card p-6 sm:p-7 shadow-xs">
              <div className="space-y-5">
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-foreground sm:text-2xl font-urban">
                    Can&apos;t find answers?
                  </h3>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    We&apos;re here to help you out whenever you need! Get in touch with our dedicated support team for personalized assistance anytime.
                  </p>
                </div>

                <a
                  href={siteConfig.links.discord}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative inline-flex h-10 sm:h-11 items-center justify-center gap-2 rounded-full bg-foreground px-6 text-sm sm:text-base font-semibold text-background shadow-xs ring-1 ring-foreground/10 transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                >
                  <span>Contact us</span>
                  <ArrowUpRight className="size-4.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </div>
          </div>

          {/* Right Column: Accordion List */}
          <div>
            <AccordionPrimitive.Root
              type="single"
              collapsible
              defaultValue="item-0"
              className="w-full space-y-3.5 sm:space-y-4"
            >
              {faqs.map((faq, index) => (
                <AccordionPrimitive.Item
                  key={index}
                  value={`item-${index}`}
                  className="group rounded-xl sm:rounded-2xl border-0 bg-muted/60 dark:bg-muted/30 hover:bg-muted/80 dark:hover:bg-muted/50 transition-colors duration-200 overflow-hidden"
                >
                  <AccordionPrimitive.Header className="flex">
                    <AccordionPrimitive.Trigger
                      className={cn(
                        "group/trigger flex flex-1 items-center justify-between gap-4 px-5 py-4 sm:px-6 sm:py-4.5 text-left text-sm sm:text-base font-semibold text-foreground transition-all hover:no-underline outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      )}
                    >
                      <span className="leading-snug">{faq.question}</span>
                      <div className="flex size-7 sm:size-8 shrink-0 items-center justify-center rounded-lg bg-foreground/10 text-foreground transition-all duration-200 group-hover/trigger:bg-foreground/15 group-data-[state=open]/trigger:bg-foreground group-data-[state=open]/trigger:text-background shadow-2xs">
                        <ArrowDown className="size-4 transition-transform duration-200 group-data-[state=open]/trigger:rotate-180" />
                      </div>
                    </AccordionPrimitive.Trigger>
                  </AccordionPrimitive.Header>
                  <AccordionPrimitive.Content className="overflow-hidden text-sm sm:text-base transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                    <div className="px-5 sm:px-6 pb-5 pt-1 text-sm sm:text-base leading-relaxed text-muted-foreground font-normal">
                      {faq.answer}
                    </div>
                  </AccordionPrimitive.Content>
                </AccordionPrimitive.Item>
              ))}
            </AccordionPrimitive.Root>
          </div>
        </div>
      </MaxWidthWrapper>
    </section>
  );
}
